<script lang="ts">
import type { HTMLAttributes, InjectionKey, Ref } from 'vue'

export type NumberFieldContentSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

export interface NumberFieldContentProps {
  class?: HTMLAttributes['class']
  disabled?: boolean
  size?: NumberFieldContentSize
  error?: boolean | string
}

export const NUMBER_FIELD_CONTENT_KEY: InjectionKey<{
  error: Ref<boolean>
  disabled: Ref<boolean>
}> = Symbol('number-field-content')
</script>

<script setup lang="ts">
import { cva } from 'class-variance-authority'
import { computed, provide } from 'vue'
import { cn } from '#ui-kit/lib/utils'

const props = withDefaults(defineProps<NumberFieldContentProps>(), {
  size: 'md',
})

provide(NUMBER_FIELD_CONTENT_KEY, { error: computed(() => !!props.error), disabled: computed(() => props.disabled) })

const fieldVariants = cva(
  'group/nf inline-flex items-center w-full rounded-lg text-primal font-display leading-none [&_input]:text-center',
  {
    variants: {
      size: {
        xs: 'h-6 text-xs',
        sm: 'h-8 text-xs',
        md: 'h-10 text-base',
        lg: 'h-[52px] text-base',
        xl: 'h-[60px] text-lg',
      },
    },
  },
)
</script>

<template>
  <div
    :data-size="props.size"
    :class="
      cn(
        fieldVariants({ size: props.size }),
        props.disabled
          ? 'cursor-not-allowed ring-0 bg-neutral-600/30 ring-neutral-600/60 text-neutral-900/30 **:pointer-events-none'
          : 'cursor-default ring-1 ring-inset ring-neutral-600 focus-within:ring-2 focus-within:ring-primary-500 [&:not(:focus-within):hover]:bg-neutral-600/10',
        props.error && 'ring-error!',
        props.class,
      )
    "
  >
    <slot />
  </div>
</template>
